// Forward-return guard.
//
// The back-trap (utils/backTrap) keeps the browser Back button driving in-app navigation
// while the user stays in the app. But once the app is genuinely LEFT — the user backs out
// past the login page, or the back-trap jail is exhausted — the app document unloads to the
// previous (blank) tab. This guard makes sure it can never be REACHED again from there:
// on unload we set `in6to_left`, and the inline head-script in index.html bounces to a
// neutral page when the app is re-entered through a history traversal
// (PerformanceNavigationTiming.type === 'back_forward'). A normal reload or a freshly
// typed URL (type 'reload' / 'navigate') is allowed through.

export const LEFT_FLAG = 'in6to_left';

// Canonical decision for the inline head-script in index.html. Pure and dependency-free so
// the head-script can mirror it verbatim and run before the bundle loads (the guard must
// not depend on module loading — a load failure would be a security hole). The drift test
// (src/__tests__/headGuard.drift.test.js) proves the inline copy never diverges from this.
//
//   'poison-bounce' — the tab carries the permanent `redirect` poison (set by
//                     redirectToSafePage): force forward past it and leave for the neutral
//                     page, so the app can never be re-reached.
//   'left-bounce'   — the app was left (`in6to_left`) and we are arriving through a
//                     Back/Forward traversal: leave for the neutral page.
//   'allow'         — a normal reload or a fresh navigation: let it through (the caller
//                     clears the leave marker).
export const decideReturnEntry = ({ state, redirectFlag, leftFlag, navType }) => {
  if (state?.redirected || redirectFlag) return 'poison-bounce';
  if (leftFlag && navType === 'back_forward') return 'left-bounce';
  return 'allow';
};

// Thin wrapper kept for the existing call sites/tests: true exactly when decideReturnEntry
// would 'left-bounce' (no redirect poison in play).
export const shouldBounceOnReturn = ({ leftFlag, navType }) =>
  decideReturnEntry({ leftFlag, navType }) === 'left-bounce';

// Arm the guard for the top-level window only. The auth iframe is cross-origin with its
// own sessionStorage, and never navigates via the browser Back/Forward, so it must not
// arm the flag. `pagehide` fires after `beforeunload` (where wipeOutTraces clears
// sessionStorage), so the marker we set here survives into the next load.
export const armBackForwardGuard = (win = window) => {
  if (win.self !== win.top) return;

  win.addEventListener('pagehide', () => {
    try {
      win.sessionStorage.setItem(LEFT_FLAG, '1');
    } catch (_e) {
      // sessionStorage can be unavailable (private mode quota, etc.); ignore.
    }
  });
};
